import React, { useState } from 'react';
import CareerCard from '../Components/CareerCard';
import MessageInput from '../Components/MessageInput';
import ChatBubble from '../Components/Chatbubble';
import aiAvatar from '../Assets/kumar-avatar.png';
import userAvatar from '../Assets/User-avatar.png';
import TypingDots from '../Components/TypingDots';
import { callOpenAI } from '../utils/openaiService';

const DiscoverCareers = () => {
  const [selectedCard, setSelectedCard] = useState('');
  const [careerDescriptions, setCareerDescriptions] = useState({});
  const [showCards, setShowCards] = useState(true);
  const [isThinking, setIsThinking] = useState(false);
  const [lastCareerMentioned, setLastCareerMentioned] = useState('');

  const [chatMessages, setChatMessages] = useState([
    {
      sender: 'ai',
      avatar: aiAvatar,
      name: 'Kumar',
      message: `Let's explore career options based on your interests! 🔍\nYou can select what feels exciting — no pressure, just vibes.`,
      timestamp: new Date().toLocaleTimeString(),
    },
  ]);

  const [careerSuggestions, setCareerSuggestions] = useState([
    '🎮 Game Designer - 📈 High Growth - 🧩 You might like this because you enjoy design + tech',
    '📱 UX Designer - 💡 Creative Thinker - 💻 Combines design + problem solving',
  ]);

  const outlinedOptions = [
    '📅 Trending / Future-ready',
    '📚 Skill-based Careers',
    '❤️ Passion-aligned Paths',
  ];

  const formatChatForOpenAI = (messages) => {
    return messages.map((msg) => ({
      role: msg.sender === 'ai' ? 'assistant' : 'user',
      content: msg.message,
    }));
  };

  const handleSelectCategory = async (value) => {
    setSelectedCard(value);
    setIsThinking(true);

    if (value.includes('Trending')) {
      setCareerSuggestions([
        '🧠 AI Prompt Engineer - Future-tech, creative logic thinker',
        '🛰️ Drone Pilot - Cutting-edge field with hands-on roles',
      ]);
    } else if (value.includes('Skill')) {
      setCareerSuggestions([
        '📊 Data Analyst - Excel, SQL & storytelling',
        '🎬 Video Editor - Visual + storytelling using Premiere Pro',
      ]);
    } else if (value.includes('Passion')) {
      setCareerSuggestions([
        '🎨 Illustrator - Artistic freelancer, design vibes',
        '🎤 Music Producer - Creative + tech = magic!',
      ]);
    }

    const fullMessages = formatChatForOpenAI(chatMessages);
    const reply = await callOpenAI({
      messages: [...fullMessages, { role: 'user', content: `I selected the category "${value}".` }],
      screenContext: 'discover',
    });

    setChatMessages((prev) => [
      ...prev,
      { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: reply, timestamp: new Date().toLocaleTimeString() },
    ]);
    setIsThinking(false);
  };

  const handleSelectCareer = async (value) => {
    setSelectedCard(value);
    setLastCareerMentioned(value);
    localStorage.setItem('selectedCareer', value);

    if (careerDescriptions[value]) return;

    setIsThinking(true);

    const fullMessages = formatChatForOpenAI(chatMessages);
    const reply = await callOpenAI({
      messages: [...fullMessages, { role: 'user', content: `Tell me more about the career: "${value}".` }],
      screenContext: 'discover',
    });

    setCareerDescriptions((prev) => ({
      ...prev,
      [value]: reply,
    }));

    setChatMessages((prev) => [
      ...prev,
      { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: reply, timestamp: new Date().toLocaleTimeString() },
    ]);
    setIsThinking(false);
  };

  const handleUserChat = async (text) => {
    if (!text.trim()) return;
    setShowCards(false);
    setIsThinking(true);

    const newUserMessage = {
      sender: 'user',
      avatar: userAvatar,
      name: 'You',
      message: text,
      timestamp: new Date().toLocaleTimeString(),
    };

    const updatedMessages = [...chatMessages, newUserMessage];
    setChatMessages(updatedMessages);

    const fullMessages = formatChatForOpenAI(updatedMessages);

    const followUpTriggers = ['how do i start', 'first step', 'help me get started', 'what should i do first'];
    const lowerText = text.toLowerCase();
    const isFollowUp = followUpTriggers.some(trigger => lowerText.includes(trigger));

    const prompt = isFollowUp && lastCareerMentioned
      ? `Give 3 beginner-friendly steps to start a career as a ${lastCareerMentioned}. Use emojis and be practical.`
      : `${text}

Now please act like a career expert and researcher. Give me a full overview of this career:
 • 📈 Career demand and future scope in India
  • 💰 Average salary (beginner vs experienced)
  • 🔮 Future trends and potential growth
  • 🌟 2-3 popular people in that field(make sure they are relevant, specific, and up-to-date)
  • 🧠 Suggest ideal personality traits for this career
  • 🤝 End with:🧭 Want to take your first real step into this career? Head over to the Career Progress section — Kumar will walk you through it, level by level.’”`;

    const reply = await callOpenAI({
      messages: [...fullMessages, { role: 'user', content: prompt }],
      screenContext: 'discover',
    });

    setChatMessages((prev) => [
      ...prev,
      { sender: 'ai', avatar: aiAvatar, name: 'Kumar', message: reply, timestamp: new Date().toLocaleTimeString() },
    ]);
    setIsThinking(false);
  };

  return (
    <div className="w-full max-w-[1000px] mx-auto p-4 sm:p-6 flex flex-col justify-between h-[85vh] sm:h-[86vh] bg-white/80 rounded-[30px] shadow-xl backdrop-blur-md">
<div className="flex flex-col gap-6 overflow-y-auto custom-scroll pr-1 flex-grow max-h-[60vh] sm:max-h-[72vh]">
<h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-syne text-stone-800 text-center">
          Discover Careers
        </h2>

        {chatMessages.map((msg, idx) => (
          <ChatBubble
            key={idx}
            sender={msg.sender}
            avatar={msg.avatar}
            name={msg.name}
            message={msg.message}
            timestamp={msg.timestamp}
          />
        ))}

        {isThinking && (
          <ChatBubble
            sender="ai"
            avatar={aiAvatar}
            name="Kumar"
            message={<TypingDots />}
          />
        )}

        {showCards && (
          <>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              {careerSuggestions.map((title, idx) => (
                <CareerCard
                  key={idx}
                  title={title}
                  variant="filled"
                  selected={selectedCard === title}
                  onClick={() => handleSelectCareer(title)}
                  description={careerDescriptions[title]}
                />
              ))}
            </div>

            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6">
              {outlinedOptions.map((title, idx) => (
                <CareerCard
                  key={idx}
                  title={title}
                  variant="outlined"
                  selected={selectedCard === title}
                  onClick={() => handleSelectCategory(title)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="mt-4">
        <MessageInput onSend={handleUserChat} />
      </div>
    </div>
  );
};

export default DiscoverCareers;